import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import DpinModal from './DpinModal';
import SearchableSelect from './SearchableSelect';

const Reports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [limit, setLimit] = useState(10);

  // Checkbox selection
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // DPIN Modal states
  const [showDpinModal, setShowDpinModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);

  // Download Modal states
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadReportId, setDownloadReportId] = useState(null);
  //before
  // const [downloadOptions, setDownloadOptions] = useState({
  //   withLetterhead: true,
  //   withDigitalSignature: true,
  //   rememberChoice: false
  // });
  // AFTER
  const [downloadOptions, setDownloadOptions] = useState({
    withLetterhead: false,
    withDigitalSignature: true,
    rememberChoice: false
  });

  // Filter states
  const [customers, setCustomers] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [filters, setFilters] = useState({
    customer: '',
    reportDate: '',
    createdDate: '',
    financialYear: getCurrentFinancialYear(),
    reportNumber: '',
    jobJointId: ''
  });

  // Helper function to get current financial year
  function getCurrentFinancialYear() {
    const today = new Date();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentYear = today.getFullYear();

    if (currentMonth >= 4) {
      // April to December: FY is current year to next year
      return `${currentYear}-${currentYear + 1}`;
    } else {
      // January to March: FY is previous year to current year
      return `${currentYear - 1}-${currentYear}`;
    }
  }

  // Generate financial years list
  function generateFinancialYears() {
    const years = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    let startYear = currentMonth >= 4 ? currentYear : currentYear - 1;

    for (let year = 2020; year <= currentYear + 1; year++) {
      years.push(`${year}-${year + 1}`);
    }

    return years;
  }

  // Load saved download preferences
  useEffect(() => {
    const savedOptions = localStorage.getItem('downloadPreferences');
    if (savedOptions) {
      setDownloadOptions(JSON.parse(savedOptions));
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
    setFinancialYears(generateFinancialYears());
    fetchReports();
  }, []);

  useEffect(() => {
    fetchReports();
  }, [currentPage, limit]);

  const fetchCustomers = async () => {
    try {
      const token = Cookies.get('token');
      // const response = await axios.get('https://app-pune-ndt.cloud/api/customers', {
      const response = await axios.get('https://app-pune-ndt.cloud/api/customers/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    setError('');

    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/reports/filter', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          limit: limit,
          customer: filters.customer,
          reportDate: filters.reportDate,
          createdDate: filters.createdDate,
          financialYear: filters.financialYear,
          reportNumber: filters.reportNumber,
          jobJointId: filters.jobJointId
        }
      });

      if (response.data.success) {
        setReports(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalReports(response.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to fetch reports');
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    fetchReports();
  };

  // Handle clear filters
  const handleClearFilters = async () => {
    const clearedFilters = {
      customer: '',
      reportDate: '',
      createdDate: '',
      financialYear: getCurrentFinancialYear(),
      reportNumber: '',
      jobJointId: ''
    };

    setFilters(clearedFilters);
    setCurrentPage(1);

    // Fetch with cleared filters immediately
    setLoading(true);
    setError('');

    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/reports/filter', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: 1,
          limit: limit,
          ...clearedFilters
        }
      });

      if (response.data.success) {
        setReports(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalReports(response.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to fetch reports');
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle checkbox selection
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    if (e.target.checked) {
      setSelectedReports(reports.map(report => report.id));
    } else {
      setSelectedReports([]);
    }
  };

  const handleSelectReport = (reportId) => {
    if (selectedReports.includes(reportId)) {
      setSelectedReports(selectedReports.filter(id => id !== reportId));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedReports, reportId];
      setSelectedReports(newSelected);
      if (newSelected.length === reports.length) {
        setSelectAll(true);
      }
    }
  };

  // Handle Edit
  const handleEdit = (reportId) => {
    navigate(`/reports/edit/${reportId}`);
  };

  // Handle Copy
  const handleCopy = async (reportId) => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/reports/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const reportData = response.data.data;

        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          const offset = date.getTimezoneOffset();
          const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
          return adjustedDate.toISOString().split('T')[0];
        };

        const copiedData = {
          customer: reportData.customer || '',
          customerClient: reportData.customer_client || '',
          backdatedReport: reportData.backdated_report || 'no',
          backDate: formatDateForInput(reportData.back_date),
          backdatedReportNumber: reportData.backdated_report_number || '',
          jobJointId: reportData.job_joint_id || '',
          reportType: reportData.report_type || 'Welding',
          materialThickness: reportData.material_thickness || '',
          reinforcement: reportData.reinforcement || '',
          materialType: reportData.material_type || 'Steel',
          materialSpecification: reportData.material_specification || '',
          procedureNumber: reportData.procedure_number || '',
          acceptanceStandard: reportData.acceptance_standard || '',
          source: reportData.source || 'Ir 192',
          sourceSize: reportData.source_size || '',
          rtSpecification: reportData.rt_specification || '',
          sourceCurie: reportData.source_curie || '',
          nb: reportData.nb || [],
          sfd: reportData.sfd || '',
          ofd: reportData.ofd || '',
          sod: reportData.sod || '',
          rtPercent: reportData.rt_percent || '',
          exposureTechnique: reportData.exposure_technique || '',
          noOfExp: reportData.no_of_exp || '',
          welderNumber: reportData.welder_number || '',
          screenFB: reportData.screen_fb || '',
          weldingProcess: reportData.welding_process || '',
          iqiPlacedOn: reportData.iqi_placed_on || '',
          viewing: reportData.viewing || '',
          filmBrand: reportData.film_brand || '',
          wireTypeIQI: reportData.wire_type_iqi || '',
          density: reportData.density || '',
          filmsCassettes: reportData.films_cassettes || '',
          holeTypeIQI: reportData.hole_type_iqi || '',
          requiredSensitivity: reportData.required_sensitivity || '',
          numberOfWallsViewed: reportData.number_of_walls_viewed || '',
          exposureTime: reportData.exposure_time || '',
          locationMarkedOn: reportData.location_marked_on || '',
          operator: reportData.operator || '',
          tagNo: reportData.tag_no || '',
          drawingNo: reportData.drawing_no || '',
          ssnNo: reportData.ssn_no || '',
          projectNo: reportData.project_no || '',
          soNo: reportData.so_no || '',
          poNo: reportData.po_no || '',
          msnNo: reportData.msn_no || '',
          otherOne: reportData.other_one || '',
          otherTwo: reportData.other_two || '',
          filmDetails: reportData.filmDetails ? reportData.filmDetails.map((film, index) => ({
            id: index + 1,
            filmSize: film.film_size_display || '',
            jointId: film.joint_id || '',
            sequence: film.sequence || '',
            observation: film.observation_text || '',
            remark: film.remark || 'Acceptable'
          })) : []
        };

        sessionStorage.setItem('copiedReportData', JSON.stringify(copiedData));
        navigate('/reports/new');
      }
    } catch (err) {
      setError('Failed to copy report');
      console.error('Error copying report:', err);
    }
  };


  const handleCustomReport = async (reportId) => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/reports/${reportId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const reportData = response.data.data;

        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          const offset = date.getTimezoneOffset();
          const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
          return adjustedDate.toISOString().split('T')[0];
        };

        const copiedData = {
          customer: reportData.customer || '',
          customerClient: reportData.customer_client || '',
          reportDate: formatDateForInput(reportData.report_date),
          reportNumber: reportData.report_number || '',
          backdatedReport: reportData.backdated_report || 'no',
          backDate: formatDateForInput(reportData.back_date),
          backdatedReportNumber: reportData.backdated_report_number || '',
          jobJointId: reportData.job_joint_id || '',
          reportType: reportData.report_type || 'Welding',
          materialThickness: reportData.material_thickness || '',
          reinforcement: reportData.reinforcement || '',
          materialType: reportData.material_type || 'Steel',
          materialSpecification: reportData.material_specification || '',
          procedureNumber: reportData.procedure_number || '',
          acceptanceStandard: reportData.acceptance_standard || '',
          source: reportData.source || 'Ir 192',
          sourceSize: reportData.source_size || '',
          rtSpecification: reportData.rt_specification || '',
          sourceCurie: reportData.source_curie || '',
          nb: reportData.nb || [],
          sfd: reportData.sfd || '',
          ofd: reportData.ofd || '',
          sod: reportData.sod || '',
          rtPercent: reportData.rt_percent || '',
          exposureTechnique: reportData.exposure_technique || '',
          noOfExp: reportData.no_of_exp || '',
          welderNumber: reportData.welder_number || '',
          screenFB: reportData.screen_fb || '',
          weldingProcess: reportData.welding_process || '',
          iqiPlacedOn: reportData.iqi_placed_on || '',
          viewing: reportData.viewing || '',
          filmBrand: reportData.film_brand || '',
          wireTypeIQI: reportData.wire_type_iqi || '',
          density: reportData.density || '',
          filmsCassettes: reportData.films_cassettes || '',
          holeTypeIQI: reportData.hole_type_iqi || '',
          requiredSensitivity: reportData.required_sensitivity || '',
          numberOfWallsViewed: reportData.number_of_walls_viewed || '',
          exposureTime: reportData.exposure_time || '',
          locationMarkedOn: reportData.location_marked_on || '',
          operator: reportData.operator || '',
          tagNo: reportData.tag_no || '',
          drawingNo: reportData.drawing_no || '',
          ssnNo: reportData.ssn_no || '',
          projectNo: reportData.project_no || '',
          soNo: reportData.so_no || '',
          poNo: reportData.po_no || '',
          msnNo: reportData.msn_no || '',
          otherOne: reportData.other_one || '',
          otherTwo: reportData.other_two || '',
          filmDetails: reportData.filmDetails ? reportData.filmDetails.map((film, index) => ({
            id: index + 1,
            filmSize: film.film_size_display || '',
            jointId: film.joint_id || '',
            sequence: film.sequence || '',
            observation: film.observation_text || '',
            remark: film.remark || 'Acceptable'
          })) : []
        };

        sessionStorage.setItem('copiedCustomReportData', JSON.stringify(copiedData));
        navigate('/custom-reports/new-custom-report');
      }
    } catch (err) {
      setError('Failed to copy report for custom report');
      console.error('Error copying report for custom:', err);
    }
  };

  // Handle Delete - Open DPIN Modal
  const handleDelete = (reportId) => {
    setReportToDelete(reportId);
    setBulkDeleteMode(false);
    setShowDpinModal(true);
  };

  // Handle Delete Confirmed
  const handleDeleteConfirmed = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.delete(
        `https://app-pune-ndt.cloud/api/reports/${reportToDelete}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccess('Report deleted successfully!');
        fetchReports();
        setReportToDelete(null);
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to delete report');
      console.error('Error deleting report:', err);
    }
  };

  // Handle Download - Open Modal
  const handleDownload = (reportId, reportNumber) => {
    setDownloadReportId({ id: reportId, number: reportNumber });
    setShowDownloadModal(true);
  };

  // Handle Download Confirm
  const handleDownloadConfirm = async () => {
    try {
      // Save preferences if remember choice is checked
      if (downloadOptions.rememberChoice) {
        localStorage.setItem('downloadPreferences', JSON.stringify(downloadOptions));
      }

      const token = Cookies.get('token');
      const response = await axios.get(
        `https://app-pune-ndt.cloud/api/reports/${downloadReportId.id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            withLetterhead: downloadOptions.withLetterhead,
            withDigitalSignature: downloadOptions.withDigitalSignature
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${downloadReportId.number.replace(/\//g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowDownloadModal(false);
      setSuccess('Report downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to download report');
      console.error('Error downloading report:', err);
    }
  };

  // Handle Bulk Delete
  const handleBulkDelete = () => {
    if (selectedReports.length === 0) {
      alert('Please select reports to delete');
      return;
    }

    setBulkDeleteMode(true);
    setShowDpinModal(true);
  };

  // Handle Bulk Delete Confirmed
  const handleBulkDeleteConfirmed = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.post(
        'https://app-pune-ndt.cloud/api/reports/bulk-delete',
        { reportIds: selectedReports },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccess(`${selectedReports.length} report(s) deleted successfully!`);
        setSelectedReports([]);
        setSelectAll(false);
        fetchReports();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (err) {
      setError('Failed to delete reports');
      console.error('Error deleting reports:', err);
    }
  };

  // Handle DPIN Success
  const handleDpinSuccess = () => {
    if (bulkDeleteMode) {
      handleBulkDeleteConfirmed();
    } else {
      handleDeleteConfirmed();
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Reports</h1>
            <div className="flex space-x-3">
              {selectedReports.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-red-700 transition"
                >
                  Delete Selected ({selectedReports.length})
                </button>
              )}
              <button
                onClick={() => navigate('/reports/new')}
                className="bg-gradient-to-r from-indigo-900 to-indigo-700 text-white px-6 py-2 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-600 transition shadow-lg"
              >
                + New Report
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Advanced Filters */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
            {/* Customer Searchable Dropdown */}
            <div>
              <SearchableSelect
                options={customers}
                value={filters.customer}
                onChange={(id) => setFilters(prev => ({ ...prev, customer: id }))}
                placeholder="All Customers"
              />
            </div>

            {/* Report Date Picker */}
            <div className="relative">
              <input
                type={filters.reportDate ? 'date' : 'text'}
                name="reportDate"
                value={filters.reportDate}
                onChange={handleFilterChange}
                onFocus={(e) => {
                  e.target.type = 'date';
                  e.target.showPicker?.();
                }}
                onBlur={(e) => {
                  if (!e.target.value) {
                    e.target.type = 'text';
                  }
                }}
                placeholder="Report Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Created Date Picker */}
            <div className="relative">
              <input
                type={filters.createdDate ? 'date' : 'text'}
                name="createdDate"
                value={filters.createdDate}
                onChange={handleFilterChange}
                onFocus={(e) => {
                  e.target.type = 'date';
                  e.target.showPicker?.();
                }}
                onBlur={(e) => {
                  if (!e.target.value) {
                    e.target.type = 'text';
                  }
                }}
                placeholder="Report Generated Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Financial Year Dropdown */}
            <div>
              <select
                name="financialYear"
                value={filters.financialYear}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">All Years</option>
                {financialYears.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Report Number Input */}
            <div>
              <input
                type="text"
                name="reportNumber"
                value={filters.reportNumber}
                onChange={handleFilterChange}
                placeholder="Report Number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Job/Joint ID Input */}
            <div>
              <input
                type="text"
                name="jobJointId"
                value={filters.jobJointId}
                onChange={handleFilterChange}
                placeholder="Job/Joint ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Items per page */}
            <div>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
            </div>

            {/* Search Button */}
            <div>
              <button
                onClick={handleSearch}
                className="w-full bg-indigo-900 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition flex items-center justify-center text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </button>
            </div>

            {/* Clear Button */}
            <div>
              <button
                onClick={handleClearFilters}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-gray-600 transition flex items-center justify-center text-sm"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-900 to-indigo-900 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Sr. No</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Report Number</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Report Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Financial Year</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Job ID</th>
                  {/* <th className="px-4 py-3 text-center text-sm font-semibold">Custom Copies</th> */}
                  <th className="px-4 py-3 text-center text-sm font-semibold">Backdated Report</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Created By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Created At</th>
                  <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-8 text-center text-gray-500">
                      Loading reports...
                    </td>
                  </tr>
                ) : reports.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="px-4 py-8 text-center text-gray-500">
                      No reports found
                    </td>
                  </tr>
                ) : (
                  reports.map((report, index) => (
                    <tr key={report.id} className="hover:bg-blue-50 transition">
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedReports.includes(report.id)}
                          onChange={() => handleSelectReport(report.id)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {totalReports - ((currentPage - 1) * limit + index)}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-blue-600">
                        {report.report_number}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {report.customer_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {formatDate(report.report_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {report.financial_year}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {report.job_joint_id}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${report.backdated_report === 'yes'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {report.backdated_report === 'yes' ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${report.status === 'Billed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                          }`}>
                          {report.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {report.created_by_name || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {formatDateTime(report.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center space-x-1">
                          {/* Edit Button */}
                          <button
                            onClick={() => handleEdit(report.id)}
                            className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                            title="Edit"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          {/* Copy Button */}
                          <button
                            onClick={() => handleCopy(report.id)}
                            className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 transition"
                            title="Copy"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>

                          {/* Custom Report Button */}
                          <button
                            onClick={() => handleCustomReport(report.id)}
                            className="p-1.5 bg-purple-500 text-white rounded hover:bg-purple-600 transition"
                            title="Custom Report"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>

                          {/* Download Button */}
                          <button
                            onClick={() => handleDownload(report.id, report.report_number)}
                            className="p-1.5 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition"
                            title="Download"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </button>

                          {/* Delete Button */}
                          <button
                            onClick={() => handleDelete(report.id)}
                            className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600 transition"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {!loading && reports.length > 0 && (
            <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * limit, totalReports)}
                  </span>{' '}
                  of <span className="font-medium">{totalReports}</span> results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className={`px-3 py-1 rounded ${currentPage === 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                  >
                    Previous
                  </button>

                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-3 py-1 rounded ${currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                      return <span key={pageNum} className="px-2">...</span>;
                    }
                    return null;
                  })}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1 rounded ${currentPage === totalPages
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Download Options Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Download Options</h2>
            <p className="text-sm text-gray-600 mb-6">
              Report: <span className="font-semibold text-indigo-600">{downloadReportId?.number}</span>
            </p>

            {/* Download Options */}
            <div className="space-y-4 mb-6">
              {/* With Letterhead */}
              <label className="flex items-start cursor-pointer group">
                <input
                  type="checkbox"
                  checked={downloadOptions.withLetterhead}
                  onChange={(e) => setDownloadOptions(prev => ({
                    ...prev,
                    withLetterhead: e.target.checked
                  }))}
                  className="mt-1 w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-indigo-600">
                    With Letterhead
                  </span>
                  <p className="text-xs text-gray-500">Include company letterhead at the top</p>
                </div>
              </label>


              {/* Remember Choice */}
              {/* <label className="flex items-start cursor-pointer group border-t pt-4">
                <input
                  type="checkbox"
                  checked={downloadOptions.rememberChoice}
                  onChange={(e) => setDownloadOptions(prev => ({
                    ...prev,
                    rememberChoice: e.target.checked
                  }))}
                  className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900 group-hover:text-green-600">
                    Remember my choice
                  </span>
                  <p className="text-xs text-gray-500">Save these preferences for future downloads</p>
                </div>
              </label> */}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3">
              <button
                onClick={handleDownloadConfirm}
                className="flex-1 bg-gradient-to-r from-indigo-900 to-indigo-900 text-white px-4 py-2 rounded-lg font-medium hover:from-indigo-800 hover:to-indigo-900 transition"
              >
                Download PDF
              </button>
              <button
                onClick={() => {
                  setShowDownloadModal(false);
                  setDownloadReportId(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DPIN Modal */}
      <DpinModal
        isOpen={showDpinModal}
        onClose={() => {
          setShowDpinModal(false);
          setReportToDelete(null);
          setBulkDeleteMode(false);
        }}
        onSuccess={handleDpinSuccess}
        itemType={bulkDeleteMode ? `${selectedReports.length} report(s)` : 'report'}
      />
    </div>
  );
};

export default Reports;


